import React from "react";
import { motion, useReducedMotion } from "motion/react";

/**
 * Bolt — the chat character (the face beside Artie's replies). Distinct from
 * Aster (the app logo). Expression states implemented here:
 *  - `idle`     — resting smile
 *  - `thinking` — eyes glance up, small mouth, pulsing ellipsis dots (shown
 *                 while the model is reasoning)
 *  - `talking`  — mouth opens and closes (shown while speaking in voice mode)
 *  - `happy`    — arc (smiling) eyes + a big grin (a playful click reaction)
 *
 * Colors do NOT follow the light/dark theme, so the character reads the same on
 * any chat background. `useReducedMotion` only gates ANIMATION, not color.
 *
 * They DO follow an embed host's brand: the --brand-mark-* variables (see
 * stampBrandMark) are stamped only when a host declares one, so the face joins
 * the host's product. Unset everywhere else — the #4caf7d green family below is
 * what the SPA paints.
 */
export type BoltState = "idle" | "thinking" | "talking" | "happy";

const HEAD = "var(--brand-mark-face, #4caf7d)";
const FEATURE = "var(--brand-mark-feature, #123a24)";
const EYE = "#ffffff";
const DOT = "var(--brand-mark-face, #4caf7d)";

// Eye pupils shift up in the thinking state (glancing up).
const PUPIL = {
  idle: { l: { cx: 18.2, cy: 25 }, r: { cx: 31.2, cy: 25 } },
  thinking: { l: { cx: 18.8, cy: 22.4 }, r: { cx: 31.8, cy: 22.4 } },
} as const;

const DOTS = [
  { cx: 37, cy: 8, r: 1.5 },
  { cx: 41, cy: 5.5, r: 1.8 },
  { cx: 45, cy: 3.5, r: 2.1 },
];

export interface BoltProps {
  size?: number;
  state?: BoltState;
  title?: string;
  /** When true the SVG is aria-hidden (a labelled wrapper provides the name). */
  decorative?: boolean;
  /** When true, the eyes blink on a ~5s cadence so the resting avatar feels
   *  alive. Used for the most recent assistant message. Gated by reduced
   *  motion (no blink when the user prefers reduced motion). */
  blink?: boolean;
}

export function Bolt({ size = 34, state = "idle", title, decorative, blink }: BoltProps) {
  const reduce = useReducedMotion() ?? false;
  const thinking = state === "thinking";
  const talking = state === "talking";
  const happy = state === "happy";
  const pupil = thinking ? PUPIL.thinking : PUPIL.idle;
  const pupilTransition = reduce
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 320, damping: 24 };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-label={
        decorative
          ? undefined
          : title ??
            (thinking
              ? "Bolt, thinking"
              : talking
              ? "Bolt, talking"
              : happy
              ? "Bolt, happy"
              : "Bolt")
      }
    >
      {/* antenna */}
      <rect x="23" y="3" width="2" height="9" rx="1" fill={HEAD} />
      <circle cx="24" cy="3" r="2.3" fill={HEAD} />
      {/* head */}
      <rect x="8" y="10" width="32" height="30" rx="12" fill={HEAD} />
      {/* open eyes (idle / thinking / talking): whites + pupils. Fades out for
          the happy pose, which uses arc eyes instead. The OUTER group carries
          the blink: the eyes collapse vertically (scaleY → ~0) for a beat, then
          repeat on a ~5s cadence. Scaling around the group's own centre
          (transform-box: fill-box + centre origin) reads as a natural blink and
          leaves the horizontal position untouched. Only fires when `blink` is
          on and motion isn't reduced. */}
      <motion.g
        data-testid="bolt-eyes"
        data-blink={blink ? "true" : "false"}
        initial={{ scaleY: 1 }}
        animate={blink && !reduce ? { scaleY: [1, 0.1, 1] } : { scaleY: 1 }}
        transition={
          blink && !reduce
            ? { duration: 0.18, times: [0, 0.5, 1], ease: "easeInOut", repeat: Infinity, repeatDelay: 5 }
            : { duration: 0 }
        }
        style={{ transformBox: "fill-box", transformOrigin: "center" }}
      >
        <motion.g initial={false} animate={{ opacity: happy ? 0 : 1 }} transition={{ duration: reduce ? 0 : 0.15 }}>
          <circle cx="17.5" cy="24" r="5" fill={EYE} />
          <circle cx="30.5" cy="24" r="5" fill={EYE} />
          <motion.circle
            r="2.5"
            fill={FEATURE}
            initial={false}
            animate={{ cx: pupil.l.cx, cy: pupil.l.cy }}
            transition={pupilTransition}
          />
          <motion.circle
            r="2.5"
            fill={FEATURE}
            initial={false}
            animate={{ cx: pupil.r.cx, cy: pupil.r.cy }}
            transition={pupilTransition}
          />
        </motion.g>
      </motion.g>
      {/* happy arc (smiling) eyes */}
      <motion.g
        initial={false}
        animate={{ opacity: happy ? 1 : 0 }}
        transition={{ duration: reduce ? 0 : 0.15 }}
        fill="none"
        stroke={FEATURE}
        strokeWidth={2.2}
        strokeLinecap="round"
      >
        <path d="M13.5 25.5 Q17.5 20 21.5 25.5" />
        <path d="M26.5 25.5 Q30.5 20 34.5 25.5" />
      </motion.g>
      {/* mouth — smile (idle) / small line (thinking) / open ellipse (talking)
          / big grin (happy) */}
      <motion.path
        d="M19 33 Q24 36 29 33"
        fill="none"
        stroke={FEATURE}
        strokeWidth={2.3}
        strokeLinecap="round"
        initial={false}
        animate={{ opacity: thinking || talking || happy ? 0 : 1 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      />
      {/* happy grin */}
      <motion.path
        d="M16.5 31.5 Q24 39.5 31.5 31.5"
        fill="none"
        stroke={FEATURE}
        strokeWidth={2.4}
        strokeLinecap="round"
        initial={false}
        animate={{ opacity: happy ? 1 : 0 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      />
      <motion.line
        x1="21.5"
        y1="34"
        x2="27.5"
        y2="32.8"
        stroke={FEATURE}
        strokeWidth={2.2}
        strokeLinecap="round"
        initial={false}
        animate={{ opacity: thinking ? 1 : 0 }}
        transition={{ duration: reduce ? 0 : 0.18 }}
      />
      {/* talking mouth — opens and closes (ry pulses) while speaking */}
      <motion.ellipse
        cx={24}
        cy={33.5}
        rx={4.2}
        fill={FEATURE}
        initial={{ opacity: 0, ry: 1 }}
        animate={
          talking
            ? { opacity: 1, ry: reduce ? 3 : [1.2, 3, 1.4, 2.8, 1.2] }
            : { opacity: 0, ry: 1 }
        }
        transition={
          talking && !reduce
            ? { ry: { duration: 0.7, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.15 } }
            : { duration: 0.15 }
        }
      />
      {/* pulsing ellipsis dots — only while thinking */}
      {thinking &&
        DOTS.map((d, i) => (
          <motion.circle
            key={i}
            cx={d.cx}
            cy={d.cy}
            r={d.r}
            fill={DOT}
            initial={{ opacity: reduce ? 0.85 : 0.3 }}
            animate={reduce ? { opacity: 0.85 } : { opacity: [0.3, 1, 0.3] }}
            transition={
              reduce
                ? { duration: 0 }
                : { duration: 1.1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }
            }
          />
        ))}
    </svg>
  );
}
